import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {Label} from './label'
import type {LabelSuggestion} from '@github-ui/item-picker-label'
import type {GraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {buildItemPickerSearchUrl} from '@github-ui/item-picker-base/api/urls'
import {SearchLabels} from '@github-ui/server-persisted-queries'
import type {SearchLabelsQuery} from '@github-ui/server-persisted-queries/types/SearchLabels'

export type GetSearchLabelsRequest = {
  owner: string
  repo: string
  query?: string
  count?: number
  labelNames?: string[]
  suggested?: boolean
  includeArchived?: boolean
}

export type GetSearchLabelsResponse = GraphQLResponse<SearchLabelsQuery>

export async function apiGetSearchLabels({
  owner,
  repo,
  query,
  count,
  labelNames,
  suggested,
  includeArchived,
}: GetSearchLabelsRequest): Promise<GetSearchLabelsResponse> {
  const params: Record<string, unknown> = {owner, repo, queryId: SearchLabels}

  if (query) {
    params['query'] = query
  }

  if (count) {
    params['count'] = count
  }

  if (labelNames && labelNames.length > 0) {
    params['labelNames'] = labelNames.join(',')
  }

  // ROLLOUT-CLEANUP(label_suggestions_endpoint): legacy delivery mechanism. The
  // picker now reads suggestions from the dedicated suggestions endpoint and
  // only falls back to this embed while that endpoint is still ramping. Remove
  // this parameter once the fallback in `api-get-label-suggestions` is deleted.
  if (suggested) {
    params['suggested'] = true
  }

  if (includeArchived) {
    params['includeArchived'] = true
  }

  const requestUrl = buildItemPickerSearchUrl('labels', params)
  const request = await reactFetchJSON(requestUrl)
  return (await request.json()) as GetSearchLabelsResponse
}

const itemPickerQueryKeyBase = 'item_picker'

export function buildItemPickerLabelSearchQueryKey(searchParams: GetSearchLabelsRequest) {
  return [itemPickerQueryKeyBase, 'labels', searchParams]
}

export type LabelPickerQueryData = {
  repositoryId: string
  labels: Label[]
  totalCount: number
  labelsByNames?: Label[]
  suggestedLabels?: LabelSuggestion[]
}

/**
 * Helper function to strip quotes from label names and optionally filter labels by those names
 * @param labelNames Array of label names that may have quotes
 * @param labels Optional array of labels to filter
 * @returns Object with stripped names and optionally filtered labels
 */
function processLabelNames(labelNames: string[], labels: Label[]) {
  const strippedNames = labelNames.map(name => name.replace(/^"/, '').replace(/"$/, ''))

  return labels.filter(label => strippedNames.includes(label.name))
}

export function transformSearchLabelResponseToQueryData(
  response: SearchLabelsQuery,
  labelNames?: string[],
): LabelPickerQueryData {
  const repository = response.repository
  const labels = repository?.labels

  if (!repository || !labels) {
    throw new Error('Invalid SearchLabels response: missing repository labels data')
  }

  const labelNodes = labels.nodes?.filter(label => label !== null) ?? []
  let matchedLabels: Label[] | undefined = repository.labelsByNames?.nodes?.filter(label => label !== null)

  // If labelNames were provided, filter to only those that match after stripping quotes
  if (labelNames && labelNames.length > 0 && matchedLabels) {
    matchedLabels = processLabelNames(labelNames, matchedLabels)
  }

  // `suggestions` is only present when the picker first opens (no search term
  // typed) and the server flag is enabled, so it's absent from the generated
  // SearchLabels type — read it defensively. Each entry is self-contained, and
  // `reason`/`rank` are preserved: the server owns the ordering and the
  // attribution, and the picker renders and reports both.
  const suggestions = (repository as RepositoryWithSuggestions).suggestions
  const suggestedLabels = suggestions
    ?.filter((entry): entry is SuggestionEntry => entry?.label != null)
    .map(entry => ({label: entry.label, reason: entry.reason, rank: entry.rank}))

  return {
    labels: labelNodes,
    repositoryId: repository.id,
    totalCount: labels.totalCount,
    labelsByNames: matchedLabels,
    suggestedLabels,
  }
}

// A self-contained suggestion entry from the server: the fully serialized label
// to suggest, a stable machine-readable reason ("popular", "user_frequent", ...)
// whose display copy the client owns, and a 0-based rank within that reason's
// group. It is not part of the generated SearchLabels GraphQL type.
type SuggestionEntry = {label: Label; reason: string; rank: number}
type RepositoryWithSuggestions = NonNullable<SearchLabelsQuery['repository']> & {
  suggestions?: Array<SuggestionEntry | null> | null
}
